// Filename: /src/components/CommentSection/Comment.js

import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext'; // Fix: Adjust path if needed
import { ArrowUpIcon, ArrowDownIcon, ChatBubbleOvalLeftIcon } from '@heroicons/react/24/solid';
import { voteOnComment } from './commentApi';
import CommentForm from './CommentForm';

// --- SUB-COMPONENTE PARA LAS RESPUESTAS (CON LÓGICA DE VOTO ACTUALIZADA) ---
const Reply = ({ reply, onReply, onVote }) => {
    const { user } = useAuth();
    const timeAgo = new Date(reply.timestamp).toLocaleString();
    
    // --- NUEVO ESTADO PARA GESTIONAR VOTOS ---
    const [userVote, setUserVote] = useState(reply.currentUserVote || null);
    const [displayVotes, setDisplayVotes] = useState(reply.upvotes - reply.downvotes);

    const handleVote = async (newVoteType) => {
        if (!user) return; // Bloqueo para usuarios no logueados

        const previousVote = userVote;
        const previousDisplayVotes = displayVotes;

        let finalVoteType = newVoteType;
        let voteDelta = 0;

        // Lógica para cancelar o cambiar voto
        if (newVoteType === previousVote) {
            finalVoteType = 'neutral'; // Cancelar voto
            voteDelta = newVoteType === 'up' ? -1 : 1;
        } else {
            if (previousVote === 'up') voteDelta--;
            if (previousVote === 'down') voteDelta++;
            if (newVoteType === 'up') voteDelta++;
            if (newVoteType === 'down') voteDelta--;
        }

        // Actualización optimista de la UI
        setUserVote(finalVoteType === 'neutral' ? null : finalVoteType);
        setDisplayVotes(prev => prev + voteDelta);

        try {
            await onVote(reply.id, finalVoteType);
        } catch (error) {
            // Si la API falla, revertir el cambio en la UI
            setUserVote(previousVote);
            setDisplayVotes(previousDisplayVotes);
            console.error("Failed to vote on reply:", error);
        }
    };
    
    return (
        <div className="flex gap-3 py-2">
            <div className="w-8 h-8 rounded-full bg-indigo-900/70 flex-shrink-0"></div>
            <div className="flex-grow">
                <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-white">{reply.author.name}</p>
                    <p className="text-xs text-gray-500">{timeAgo}</p>
                </div>
                <p className="text-sm text-gray-300 mt-1">{reply.content}</p>
                <div className={`flex items-center gap-4 mt-2 text-xs text-gray-400 ${!user ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-1">
                        <ArrowUpIcon 
                            onClick={() => handleVote('up')}
                            className={`w-4 h-4 transition-colors ${user ? 'cursor-pointer' : 'cursor-default'} ${userVote === 'up' ? 'text-green-500' : 'hover:text-white'}`} 
                        />
                        <span className="font-semibold">{displayVotes}</span>
                        <ArrowDownIcon 
                            onClick={() => handleVote('down')}
                            className={`w-4 h-4 transition-colors ${user ? 'cursor-pointer' : 'cursor-default'} ${userVote === 'down' ? 'text-red-500' : 'hover:text-white'}`}
                        />
                    </div>
                    {user && (
                        <button onClick={() => onReply({ id: reply.id, author: reply.author })} className="flex items-center gap-1 hover:text-white">
                            <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                            <span>Reply</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};


// --- COMPONENTE PRINCIPAL (CON LÓGICA DE VOTO ACTUALIZADA) ---
const Comment = ({ comment, onPostReply }) => {
    const { user } = useAuth();
    const [isReplying, setIsReplying] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null);

    // --- NUEVO ESTADO PARA GESTIONAR VOTOS ---
    const [userVote, setUserVote] = useState(comment.currentUserVote || null);
    const [displayVotes, setDisplayVotes] = useState(comment.upvotes - comment.downvotes);
    
    // La lógica de `handleVote` ahora es más robusta para el comentario principal
    const handleVote = async (newVoteType) => {
        if (!user) return;

        const previousVote = userVote;
        const previousDisplayVotes = displayVotes;

        let finalVoteType = newVoteType;
        let voteDelta = 0;

        if (newVoteType === previousVote) {
            finalVoteType = 'neutral';
            voteDelta = newVoteType === 'up' ? -1 : 1;
        } else {
            if (previousVote === 'up') voteDelta--;
            if (previousVote === 'down') voteDelta++;
            if (newVoteType === 'up') voteDelta++;
            if (newVoteType === 'down') voteDelta--;
        }

        setUserVote(finalVoteType === 'neutral' ? null : finalVoteType);
        setDisplayVotes(prev => prev + voteDelta);

        try {
            await voteOnComment(comment.id, finalVoteType);
        } catch (error) {
            setUserVote(previousVote);
            setDisplayVotes(previousDisplayVotes);
            console.error("Failed to vote on comment:", error);
        }
    };
    
    // Las funciones `handleStartReply` y `handleReplySubmit` no cambian
    const handleStartReply = (target) => {
        setReplyingTo(target);
        setIsReplying(true);
    };
    
    const handleReplySubmit = async (content) => {
        const finalContent = replyingTo.id !== comment.id ? `@${replyingTo.author.name} ${content}` : content;
        await onPostReply({
            content: finalContent,
            parentId: comment.id,
            replyingTo: { id: replyingTo.id, authorName: replyingTo.author.name }
        });
        setIsReplying(false);
        setReplyingTo(null);
    };

    // La lógica `processedReplies` no cambia
    const processedReplies = useMemo(() => {
        if (!comment.replies || comment.replies.length === 0) return [];
        const replyMap = new Map();
        comment.replies.forEach(reply => {
            const parentId = reply.replyingTo?.id || comment.id;
            if (!replyMap.has(parentId)) replyMap.set(parentId, []);
            replyMap.get(parentId).push(reply);
        });
        const topLevelReplies = (replyMap.get(comment.id) || [])
            .sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes));
        const finalReplies = [];
        topLevelReplies.forEach(topReply => {
            finalReplies.push(topReply);
            const children = replyMap.get(topReply.id) || [];
            finalReplies.push(...children.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
        });
        return finalReplies;
    }, [comment.replies, comment.id]);

    const timeAgo = new Date(comment.timestamp).toLocaleString();

    return (
        <div className="flex gap-3 pt-4">
            <div className="w-8 h-8 rounded-full bg-indigo-800 flex-shrink-0"></div>
            <div className="flex-grow">
                <div className="flex items-center gap-2">
                    <p className="text-xs font-bold text-white">{comment.author.name}</p>
                    <p className="text-xs text-gray-500">{timeAgo}</p>
                </div>
                <p className="text-sm text-gray-300 mt-1">{comment.content}</p>
                <div className={`flex items-center gap-4 mt-2 text-xs text-gray-400 ${!user ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-1">
                        <ArrowUpIcon 
                             onClick={() => handleVote('up')}
                             className={`w-4 h-4 transition-colors ${user ? 'cursor-pointer' : 'cursor-default'} ${userVote === 'up' ? 'text-green-500' : 'hover:text-white'}`}
                        />
                        <span className="font-semibold">{displayVotes}</span>
                        <ArrowDownIcon 
                             onClick={() => handleVote('down')}
                             className={`w-4 h-4 transition-colors ${user ? 'cursor-pointer' : 'cursor-default'} ${userVote === 'down' ? 'text-red-500' : 'hover:text-white'}`}
                        />
                    </div>
                    {user && (
                        <button onClick={() => handleStartReply({ id: comment.id, author: comment.author })} className="flex items-center gap-1 hover:text-white">
                            <ChatBubbleOvalLeftIcon className="w-4 h-4" />
                            <span>Reply</span>
                        </button>
                    )}
                </div>

                <div className="mt-2 pl-4 border-l-2 border-gray-800">
                    {processedReplies.map(reply => (
                        <Reply 
                            key={reply.id} 
                            reply={reply}
                            onVote={voteOnComment} // Pasa la función de la API directamente
                            onReply={handleStartReply} 
                        />
                    ))}
                     {isReplying && (
                        <div className="mt-4">
                            <CommentForm
                                onSubmit={handleReplySubmit}
                                placeholder={`Replying to ${replyingTo.author.name}...`}
                                cta="Reply"
                                onCancel={() => setIsReplying(false)}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Comment;